import { Sparkles, Infinity as InfinityIcon, CreditCard, ExternalLink, Settings, ChevronDown, Zap } from 'lucide-react';
import { useState } from 'react';
import { useCredits } from '@/contexts/CreditsContext';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface CreditDisplayProps {
    onUpgradeClick?: () => void;
    className?: string;
}

export function CreditDisplay({
    onUpgradeClick,
    className,
}: CreditDisplayProps) {
    const { credits, subscriptionTier, isUnlimited, isLoading } = useCredits();
    const [isLoadingPortal, setIsLoadingPortal] = useState(false);
    const { toast } = useToast();

    const handleManageSubscription = async () => {
        setIsLoadingPortal(true);
        try {
            const { data, error } = await supabase.functions.invoke('billing-portal', {
                body: { returnUrl: window.location.href },
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Error opening customer portal:', error);
            toast({
                title: 'Error',
                description: 'Could not open subscription management. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsLoadingPortal(false);
        }
    };

    if (isLoading) {
        return (
            <div className={cn('flex items-center gap-2 px-2 py-1.5', className)}>
                <div className="h-3 w-14 animate-pulse rounded bg-white/10" />
            </div>
        );
    }

    const hasPaidSubscription = subscriptionTier !== 'free';

    return (
        <div className={cn('flex flex-col gap-1', className)}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={cn(
                            'flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 w-full',
                            'bg-white/[0.03] border border-white/[0.06]',
                            'transition-all duration-150 hover:bg-white/[0.06] hover:border-white/10',
                            'focus:outline-none focus:ring-1 focus:ring-violet-500/30'
                        )}
                    >
                        <div className="flex items-center gap-2">
                            {isUnlimited ? (
                                <>
                                    <div className="flex items-center justify-center rounded-md bg-purple-500/15 p-1">
                                        <InfinityIcon className="h-3 w-3 text-purple-400" />
                                    </div>
                                    <span className="text-xs font-medium text-purple-300">
                                        Unlimited
                                    </span>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-center rounded-md bg-amber-500/15 p-1">
                                        <Zap className="h-3 w-3 text-amber-400" />
                                    </div>
                                    <span className={cn(
                                        'text-xs font-medium',
                                        credits < 1 ? 'text-amber-400' : 'text-zinc-300'
                                    )}>
                                        {credits.toFixed(2)} credits
                                    </span>
                                </>
                            )}
                            {subscriptionTier === 'pro' && (
                                <span className="rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-violet-400 uppercase tracking-wide">
                                    Pro
                                </span>
                            )}
                            {subscriptionTier === 'unlimited' && (
                                <span className="rounded-full bg-purple-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-purple-400 uppercase tracking-wide">
                                    ∞
                                </span>
                            )}
                        </div>
                        <ChevronDown className="h-3 w-3 text-zinc-500" />
                    </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    align="start"
                    className="w-60 bg-[#12121a] border-white/10 rounded-xl p-0 overflow-hidden"
                >
                    {/* Header with credits info */}
                    <div className="px-4 py-3 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border-b border-white/5">
                        <div className="flex items-center gap-2 mb-2">
                            {isUnlimited ? (
                                <InfinityIcon className="h-4 w-4 text-purple-400" />
                            ) : (
                                <Sparkles className="h-4 w-4 text-amber-400" />
                            )}
                            <span className="text-sm font-semibold text-white">
                                {isUnlimited ? 'Unlimited Plan' : `${credits.toFixed(2)} Credits`}
                            </span>
                        </div>
                        <div className="space-y-0.5 text-[11px] text-zinc-400">
                            <p className="flex justify-between">
                                <span>New generation</span>
                                <span className="text-zinc-300">1 credit</span>
                            </p>
                            <p className="flex justify-between">
                                <span>Edit / refine</span>
                                <span className="text-zinc-300">1 credit</span>
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="p-1.5">
                        {hasPaidSubscription && (
                            <DropdownMenuItem
                                onClick={handleManageSubscription}
                                disabled={isLoadingPortal}
                                className="cursor-pointer rounded-lg px-3 py-2.5 text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 focus:bg-white/5"
                            >
                                <Settings className="mr-2.5 h-3.5 w-3.5 text-zinc-500" />
                                <span>Manage Subscription</span>
                                <ExternalLink className="ml-auto h-3 w-3 text-zinc-600" />
                            </DropdownMenuItem>
                        )}

                        {subscriptionTier === 'free' && onUpgradeClick && (
                            <DropdownMenuItem
                                onClick={onUpgradeClick}
                                className="cursor-pointer rounded-lg px-3 py-2.5 text-xs font-medium bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 text-violet-300 hover:text-violet-200 hover:from-violet-600/30 hover:to-fuchsia-600/30 focus:from-violet-600/30 focus:to-fuchsia-600/30 border border-violet-500/20"
                            >
                                <CreditCard className="mr-2.5 h-3.5 w-3.5" />
                                <span>Upgrade to Pro</span>
                            </DropdownMenuItem>
                        )}

                        {subscriptionTier === 'pro' && onUpgradeClick && (
                            <DropdownMenuItem
                                onClick={onUpgradeClick}
                                className="cursor-pointer rounded-lg px-3 py-2.5 text-xs font-medium text-zinc-300 hover:text-white hover:bg-white/5 focus:bg-white/5"
                            >
                                <CreditCard className="mr-2.5 h-3.5 w-3.5 text-zinc-500" />
                                <span>Get More Credits</span>
                            </DropdownMenuItem>
                        )}
                    </div>

                    {/* Footer hint */}
                    {subscriptionTier === 'free' && (
                        <div className="px-4 py-2.5 bg-white/[0.02] border-t border-white/5">
                            <p className="text-[10px] text-zinc-500 text-center">
                                Free tier • 5 credits/month
                            </p>
                        </div>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {credits < 1 && !isUnlimited && (
                <p className="px-1 text-[9px] text-amber-400/70 font-medium">
                    Running low on credits
                </p>
            )}
        </div>
    );
}
